// 文件方块上的「i」：这份文件怎么准备 —— 样本、怎么拿到、发给客户的话（一键复制）、要求。
// 新文员照着做就行；管理员在这里写指南、传样本（改的是任务类型，所有任务共用）。
import { useState } from "react";
import type { FileGuide, ManifestFileReq, ManifestSchema, TaskFileDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { mediaUrl } from "../lib/net";
import { Badge } from "./Badges";
import { useFilePreview } from "./FilePreview";

const isImage = (t: string, name: string) => t.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(name);

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // 非 https / 老浏览器：退回选中复制
    const el = document.createElement("textarea");
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    el.remove();
  }
}

export function FileGuideDialog({ typeId, schema, cat, taskFiles, isAdmin, token, onChanged, onClose }: {
  typeId: string; schema: ManifestSchema; cat: ManifestFileReq; taskFiles: TaskFileDTO[]; isAdmin: boolean; token: string | null;
  onChanged: () => Promise<void> | void; onClose: () => void;
}) {
  const g: FileGuide = cat.guide ?? {};
  const samples = g.samples ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ howToGet: g.howToGet ?? "", clientMessage: g.clientMessage ?? "", requirements: g.requirements ?? "" });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useFilePreview();
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); await onChanged(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const saveText = () => run(async () => {
    const guide: FileGuide = { ...g };
    for (const k of ["howToGet", "clientMessage", "requirements"] as const) {
      const v = draft[k].trim();
      if (v) guide[k] = v; else delete guide[k];
    }
    await api.updateType(typeId, { manifestSchema: { ...schema, version: schema.version + 1, files: schema.files.map((f) => (f.key === cat.key ? { ...f, guide } : f)) } });
    setEditing(false);
  });
  const sampleUrl = (id: string) => mediaUrl(`/api/types/${typeId}/files/${cat.key}/samples/${id}`, token);
  const mine = taskFiles.filter((f) => f.key === cat.key && f.state === "synced");
  const fmt = (cat.accept ?? []).map((a) => a.replace(/^.*\//, "").replace("jpeg", "jpg").toUpperCase()).filter((v, i, l) => l.indexOf(v) === i);
  const empty = !g.howToGet && !g.clientMessage && !g.requirements && !samples.length;

  return (
    <div className="modal-mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal fg-modal" role="dialog" aria-modal="true" aria-label={`怎么准备 ${cat.label}`}>
        <div className="row" style={{ gap: 6 }}>
          <h2 style={{ margin: 0 }}>怎么准备「{cat.label}」</h2>
          {cat.required ? <Badge tone="red">必需</Badge> : <Badge tone="gray">可选</Badge>}
          {cat.submitToGov && <Badge tone="amber">上传政府</Badge>}
          <span className="spacer" />
          {isAdmin && !editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>✎ 编辑指南</button>}
        </div>
        {cat.hint && <p className="small muted">{cat.hint}</p>}
        {empty && !editing && <p className="notice">还没有写准备指南{isAdmin ? "：点「编辑指南」写怎么拿、发给客户的话和要求，再传一份样本。" : "，请找管理员补上。"}</p>}

        <section className="fg-sec">
          <h3>样本{samples.length ? `（${samples.length}）` : ""}</h3>
          {samples.length ? (
            <div className="fg-samples">
              {samples.map((s) => (
                <div key={s.id} className="fg-sample">
                  {/* 点一下（双击也行）在页面里预览，不下载 */}
                  <button type="button" className="fg-open" title="预览" data-testid="sample-open" onClick={() => { const url = sampleUrl(s.id); if (url) preview.open({ url, name: s.name, contentType: s.contentType }); }}>
                    {isImage(s.contentType, s.name) ? <img src={sampleUrl(s.id)} alt={s.name} /> : <span className="fg-pdf">{(s.name.match(/\.(\w+)$/)?.[1] ?? "文件").toUpperCase()}</span>}
                  </button>
                  <div className="small fg-sname" title={s.name} translate="no">{s.name}</div>
                  {isAdmin && (
                    <div className="row" style={{ gap: 4 }}>
                      <label className="btn btn-sm" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined} title="换一份样本（先传新的，再删这份）">
                        替换<input type="file" hidden accept="image/*,application/pdf" aria-label={`替换样本 ${s.name}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void run(async () => { await api.addSample(typeId, cat.key, f); await api.removeSample(typeId, cat.key, s.id); }); }} />
                      </label>
                      <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => { if (confirm(`删除样本 ${s.name}？`)) void run(() => api.removeSample(typeId, cat.key, s.id)); }}>删除</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : <p className="small muted">还没有样本。</p>}
          {isAdmin && (
            <div className="row" style={{ gap: 6, marginTop: 6 }}>
              <label className="btn btn-sm" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
                ＋ 上传样本<input type="file" hidden accept="image/*,application/pdf" aria-label="上传样本" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void run(() => api.addSample(typeId, cat.key, f)); }} />
              </label>
              {mine.length > 0 && (
                <select aria-label="从本任务文件设为样本" value="" disabled={busy} onChange={(e) => { const id = e.target.value; if (id) void run(() => api.sampleFromFile(typeId, cat.key, id)); }} style={{ width: "auto" }}>
                  <option value="">从这个任务已传的文件设为样本…</option>
                  {mine.map((f) => <option key={f.id} value={f.id}>{f.name.split("／").pop()}</option>)}
                </select>
              )}
              <span className="small muted">样本所有人都能看到；每份样本可以「替换」成打码版。</span>
            </div>
          )}
        </section>

        <section className="fg-sec">
          <h3>怎么拿到这份文件</h3>
          {editing ? <textarea aria-label="怎么拿到" rows={4} value={draft.howToGet} onChange={(e) => setDraft({ ...draft, howToGet: e.target.value })} placeholder="例如：让客户用手机拍护照资料页（有照片那一页），或在 xx 网站下载" />
            : g.howToGet ? <div className="pre">{g.howToGet}</div> : <p className="small muted">（未填写）</p>}
        </section>

        <section className="fg-sec">
          <div className="row"><h3 style={{ margin: 0 }}>发给客户的话</h3><span className="spacer" />
            {!editing && g.clientMessage && <button className="btn btn-sm btn-primary" onClick={() => void copyText(g.clientMessage!).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}>{copied ? "已复制 ✓" : "复制"}</button>}
          </div>
          {editing ? <textarea aria-label="发给客户的话" rows={5} value={draft.clientMessage} onChange={(e) => setDraft({ ...draft, clientMessage: e.target.value })} placeholder="直接复制发给客户的原话，例如：您好，麻烦拍一张护照资料页发给我们，四个角要拍全、不要反光，谢谢！" />
            : g.clientMessage ? <div className="pre fg-msg">{g.clientMessage}</div> : <p className="small muted">（未填写）</p>}
        </section>

        <section className="fg-sec">
          <h3>要求</h3>
          <ul className="small fg-req">
            <li>{cat.required ? "必需：没有这份文件不能开始" : "可选：有就传，没有可以不传"}</li>
            {fmt.length > 0 && <li>格式：{fmt.join(" / ")}</li>}
            {cat.multiple && <li>可以传多份（多页分开拍也行）</li>}
            {cat.submitToGov && <li>会原样上传到政府网站：要清晰、完整、不能有涂改</li>}
          </ul>
          {editing ? <textarea aria-label="要求" rows={4} value={draft.requirements} onChange={(e) => setDraft({ ...draft, requirements: e.target.value })} placeholder="例如：彩色、四角完整、不反光；中文文件要附英文翻译；有效期 6 个月以上" />
            : g.requirements && <div className="pre">{g.requirements}</div>}
        </section>

        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          {editing ? (
            <>
              <button className="btn" disabled={busy} onClick={() => { setEditing(false); setDraft({ howToGet: g.howToGet ?? "", clientMessage: g.clientMessage ?? "", requirements: g.requirements ?? "" }); }}>取消</button>
              <button className="btn btn-primary" disabled={busy} onClick={() => void saveText()}>{busy ? "保存中…" : "保存指南"}</button>
            </>
          ) : <button className="btn" onClick={onClose}>关闭</button>}
        </div>
      </div>
      {preview.node}
    </div>
  );
}
